import { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

import {
  RequireMembershipStarted,
  RequireReadyForPayment,
  RequireTermsAccepted,
} from './components/Membership/route-guard';

import AOS from 'aos';
import 'aos/dist/aos.css';

import TrackNumberModal from './components/Modals/TrackNumberModal';
import { ErrorBoundary } from '@/components/dev/ErrorBoundary';

// Lazy-loaded routes/components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const News = lazy(() => import('./pages/News'));
const NewsArticle = lazy(() => import('./components/News/NewsArticle'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const MembershipApplication = lazy(
  () => import('./pages/MembershipApplication'),
);
const Payment = lazy(() => import('./pages/Payment'));
const TrackStatus = lazy(() => import('./pages/TrackStatus'));
const BookAppointment = lazy(() => import('./pages/BookAppointment'));
const FeaturedEvent = lazy(() => import('./components/Home/FeaturedEvents'));

const App = () => {
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackCode, setTrackCode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    try {
      AOS.init({ duration: 800, once: true });
    } catch (e) {
      console.warn('AOS init failed:', e);
    }
  }, []);

  const handleTrack = () => {
    const code = trackCode?.trim();
    if (code) {
      // Prefer client-side routing over full reloads for performance & UX
      navigate(`/track?tn=${encodeURIComponent(code)}`);
      setShowTrackModal(false);
    }
  };

  const handleOpenAdvisory = () => {
    navigate('/featured-events#advisory');
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar onStatusClick={() => setShowTrackModal(true)} />

      <main className="flex-1">
        <ErrorBoundary>
          <Suspense
            fallback={<div className="p-6 text-sm text-gray-600">Loading…</div>}
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsArticle />} />
              <Route path="/track" element={<TrackStatus />} />
              <Route path="/book-appointment" element={<BookAppointment />} />
              <Route path="/membership" element={<MembershipApplication />} />
              <Route
                path="/featured-events"
                element={<FeaturedEvent onOpenAdvisory={handleOpenAdvisory} />}
              />
              <Route
                path="/terms"
                element={
                  <RequireMembershipStarted>
                    <TermsAndConditions />
                  </RequireMembershipStarted>
                }
              />
              <Route
                path="/payment"
                element={
                  <RequireMembershipStarted>
                    <RequireTermsAccepted>
                      <RequireReadyForPayment>
                        <Payment />
                      </RequireReadyForPayment>
                    </RequireTermsAccepted>
                  </RequireMembershipStarted>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer />

      <TrackNumberModal
        open={showTrackModal}
        onClose={() => setShowTrackModal(false)}
        value={trackCode}
        setValue={setTrackCode}
        onTrack={handleTrack}
      />
    </div>
  );
};

export default App;
